import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { SimDailyRow, SimMonthlyRow } from "@/hooks/useSims";
import {
  MAX_CHART_SERIES,
  dayKey,
  formatDataMb,
  lastDayKeys,
  lastMonthKeys,
  monthKeyFromEpoch,
  seriesColor,
  simSeriesName,
  type SimCard,
} from "@/lib/sims";

interface Series {
  key: string;
  label: string;
  simIds: string[];
  color: string;
}

/** One series per SIM (fixed order by slot); beyond the cap → "Other". */
function buildSeries(sims: SimCard[]): Series[] {
  const sorted = [...sims].sort(
    (a, b) =>
      (a.slot ?? 999) - (b.slot ?? 999) || a.iccid.localeCompare(b.iccid),
  );
  const series: Series[] = sorted
    .slice(0, MAX_CHART_SERIES)
    .map((sim, index) => ({
      key: `s${index}`,
      label: simSeriesName(sim),
      simIds: [sim.id],
      color: seriesColor(index),
    }));
  if (sorted.length > MAX_CHART_SERIES) {
    series.push({
      key: "other",
      label: "Other",
      simIds: sorted.slice(MAX_CHART_SERIES).map((s) => s.id),
      color: seriesColor(MAX_CHART_SERIES),
    });
  }
  return series;
}

type Point = { period: string; label: string; estimated?: boolean } & Record<
  string,
  number | string | boolean | undefined
>;

function buildPoints<T extends { simCardID: string; dataMb: number }>(
  keys: { key: string; label: string }[],
  rows: T[],
  rowKey: (row: T) => string,
  series: Series[],
  estimated?: (row: T) => boolean,
): Point[] {
  const seriesBySim = new Map<string, string>();
  for (const s of series) for (const id of s.simIds) seriesBySim.set(id, s.key);
  const points = new Map<string, Point>(
    keys.map(({ key, label }) => [
      key,
      Object.fromEntries([
        ["period", key],
        ["label", label],
        ...series.map((s) => [s.key, 0]),
      ]) as Point,
    ]),
  );
  for (const row of rows) {
    const point = points.get(rowKey(row));
    const seriesKey = seriesBySim.get(row.simCardID);
    if (!point || !seriesKey) continue;
    point[seriesKey] =
      ((point[seriesKey] as number) ?? 0) + Number(row.dataMb ?? 0);
    if (estimated?.(row)) point.estimated = true;
  }
  return Array.from(points.values());
}

function UsageBarChart({
  title,
  subtitle,
  points,
  series,
  testId,
}: {
  title: string;
  subtitle: string;
  points: Point[];
  series: Series[];
  testId: string;
}) {
  const [showTable, setShowTable] = useState(false);
  const config = useMemo(
    () =>
      Object.fromEntries(
        series.map((s) => [s.key, { label: s.label, color: s.color }]),
      ) as ChartConfig,
    [series],
  );
  const total = points.reduce(
    (sum, p) =>
      sum + series.reduce((acc, s) => acc + ((p[s.key] as number) ?? 0), 0),
    0,
  );

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground">
            {subtitle} · total {formatDataMb(total)}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => setShowTable((v) => !v)}
        >
          {showTable ? "Show chart" : "Show table"}
        </Button>
      </div>
      {showTable ? (
        <div className="max-h-[260px] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                {series.map((s) => (
                  <TableHead key={s.key} className="text-right">
                    {s.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {points.map((p) => (
                <TableRow key={p.period}>
                  <TableCell>
                    {p.label}
                    {p.estimated ? " (estimated)" : ""}
                  </TableCell>
                  {series.map((s) => (
                    <TableCell key={s.key} className="text-right tabular-nums">
                      {formatDataMb(p[s.key] as number)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ChartContainer config={config} className="h-[220px] w-full">
          <BarChart data={points} barCategoryGap={2}>
            <CartesianGrid vertical={false} strokeOpacity={0.4} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              minTickGap={16}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              width={56}
              tickFormatter={(v: number) => formatDataMb(v)}
            />
            <ChartTooltip
              cursor={{ fillOpacity: 0.15 }}
              content={
                <ChartTooltipContent
                  labelFormatter={(_, payload) => {
                    const point = payload?.[0]?.payload as Point | undefined;
                    return point
                      ? `${point.label}${point.estimated ? " (estimated)" : ""}`
                      : "";
                  }}
                  formatter={(value, name) => (
                    <div className="flex w-full justify-between gap-3">
                      <span className="text-muted-foreground">
                        {config[name as string]?.label ?? name}
                      </span>
                      <span className="font-mono tabular-nums text-foreground">
                        {formatDataMb(value as number)}
                      </span>
                    </div>
                  )}
                />
              }
            />
            {series.length > 1 && (
              <ChartLegend content={<ChartLegendContent />} />
            )}
            {series.map((s, index) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                stackId="usage"
                fill={`var(--color-${s.key})`}
                stroke="hsl(var(--card))"
                strokeWidth={1}
                radius={index === series.length - 1 ? [4, 4, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
}

const MONTH_FORMAT = new Intl.DateTimeFormat("en", {
  month: "short",
  year: "2-digit",
  timeZone: "UTC",
});
const DAY_FORMAT = new Intl.DateTimeFormat("en", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

/** Daily (30 days) and monthly (12 cycles) usage, stacked per SIM. */
export function SimUsageCharts({
  sims,
  daily,
  monthly,
}: {
  sims: SimCard[];
  daily: SimDailyRow[];
  monthly: SimMonthlyRow[];
}) {
  const series = useMemo(() => buildSeries(sims), [sims]);
  const dailyPoints = useMemo(
    () =>
      buildPoints(
        lastDayKeys(30).map((key) => ({
          key,
          label: DAY_FORMAT.format(new Date(`${key}T00:00:00Z`)),
        })),
        daily,
        (row) => dayKey(row.day),
        series,
        (row) => row.isEstimated,
      ),
    [daily, series],
  );
  const monthlyPoints = useMemo(
    () =>
      buildPoints(
        lastMonthKeys(12).map((key) => ({
          key,
          label: MONTH_FORMAT.format(new Date(`${key}T00:00:00Z`)),
        })),
        monthly,
        (row) => monthKeyFromEpoch(row.cycleMonth),
        series,
      ),
    [monthly, series],
  );

  if (sims.length === 0) return null;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <UsageBarChart
        title="Daily data usage"
        subtitle="Last 30 days, derived from Simetric updates"
        points={dailyPoints}
        series={series}
        testId="sim-usage-daily"
      />
      <UsageBarChart
        title="Monthly data usage"
        subtitle="Last 12 billing cycles"
        points={monthlyPoints}
        series={series}
        testId="sim-usage-monthly"
      />
    </div>
  );
}
